import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  Shield, FileText, User, Briefcase, Eye, Lock, Database, Bell, 
  Globe, Mail, ChevronRight, ChevronDown, CheckCircle, AlertCircle,
  CreditCard, Truck, Users, Scale, Baby, Clock, ExternalLink, Phone
} from 'lucide-react';

const POLICY_SECTIONS = [
  {
    id: 'scope',
    title: 'Scope of Policy',
    icon: Globe,
    color: 'blue'
  },
  {
    id: 'information-collected',
    title: 'Information We Collect',
    icon: Database,
    color: 'purple'
  },
  {
    id: 'persona-verification',
    title: 'Third-Party Identity Verification (Persona)',
    icon: Shield,
    color: 'green'
  },
  {
    id: 'profiles',
    title: 'Platform Profiles',
    icon: User,
    color: 'cyan'
  },
  {
    id: 'ratings',
    title: 'Ratings and Reviews',
    icon: Eye,
    color: 'yellow'
  },
  {
    id: 'jobs',
    title: 'Job Search and Recruitment',
    icon: Briefcase,
    color: 'indigo'
  },
  {
    id: 'credentials',
    title: 'Credential Verification',
    icon: FileText,
    color: 'teal'
  },
  {
    id: 'subscriptions',
    title: 'Subscription Services',
    icon: CreditCard,
    color: 'pink'
  },
  {
    id: 'auto-collected',
    title: 'Information Automatically Collected',
    icon: Database,
    color: 'orange'
  },
  {
    id: 'how-we-use',
    title: 'How We Use Information',
    icon: CheckCircle,
    color: 'emerald'
  },
  {
    id: 'sharing',
    title: 'Sharing of Information',
    icon: Users,
    color: 'violet'
  },
  {
    id: 'security',
    title: 'Data Security',
    icon: Lock,
    color: 'red'
  },
  {
    id: 'user-rights',
    title: 'User Rights',
    icon: Scale,
    color: 'blue'
  },
  {
    id: 'california',
    title: 'California Privacy Rights',
    icon: Scale,
    color: 'amber'
  },
  {
    id: 'children',
    title: "Children's Privacy",
    icon: Baby,
    color: 'rose'
  },
  {
    id: 'retention',
    title: 'Data Retention',
    icon: Clock,
    color: 'slate'
  },
  {
    id: 'third-party',
    title: 'Third-Party Websites',
    icon: ExternalLink,
    color: 'gray'
  },
  {
    id: 'changes',
    title: 'Changes to This Policy',
    icon: Bell,
    color: 'sky'
  },
  {
    id: 'contact',
    title: 'Contact Information',
    icon: Mail,
    color: 'green'
  }
];

const SectionCard = ({ id, title, icon: Icon, color, children, isExpanded, onToggle }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden"
      id={id}
    >
      <button
        onClick={onToggle}
        className={`w-full p-5 flex items-center justify-between text-left hover:bg-slate-50 transition-colors`}
      >
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl bg-${color}-100`}>
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
          <h2 className="font-heading text-lg font-bold text-slate-900">{title}</h2>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-5 w-5 text-slate-500" />
        ) : (
          <ChevronRight className="h-5 w-5 text-slate-500" />
        )}
      </button>
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="px-5 pb-5"
        >
          <div className="pl-16 border-l-2 border-slate-100">
            {children}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

const BulletList = ({ items }) => (
  <ul className="space-y-2 mt-3">
    {items.map((item, idx) => (
      <li key={item} className="flex items-start gap-2 text-slate-600">
        <CheckCircle className="h-4 w-4 text-green-500 mt-1 flex-shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const PrivacyPolicy = () => {
  const [expandedSections, setExpandedSections] = useState(
    POLICY_SECTIONS.reduce((acc, section) => ({ ...acc, [section.id]: true }), {})
  );

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const expandAll = () => {
    setExpandedSections(POLICY_SECTIONS.reduce((acc, section) => ({ ...acc, [section.id]: true }), {}));
  };

  const collapseAll = () => {
    setExpandedSections(POLICY_SECTIONS.reduce((acc, section) => ({ ...acc, [section.id]: false }), {}));
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />
      
      <main className="flex-1">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
            >
              <Shield className="h-5 w-5 text-green-400" />
              <span className="text-sm font-medium">Your Privacy Matters</span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-heading text-4xl sm:text-5xl font-bold mb-4"
            >
              Privacy Policy
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-slate-300 text-lg max-w-2xl mx-auto mb-6"
            >
              Straight Connect values the privacy and security of all users of our platform. 
              Learn how we collect, use, and protect your information.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="flex flex-wrap justify-center gap-4 text-sm"
            >
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <span className="text-slate-400">Effective Date:</span>{' '}
                <span className="font-medium">March 16, 2026</span>
              </div>
              <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <span className="text-slate-400">Last Updated:</span>{' '}
                <span className="font-medium">March 16, 2026</span>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600 hidden sm:block">
                Jump to a section or scroll down to read
              </p>
              <div className="flex gap-2">
                <button
                  onClick={expandAll}
                  className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Expand All
                </button>
                <button
                  onClick={collapseAll}
                  className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Collapse All
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Introduction */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6 mb-8"
          >
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 p-3 rounded-xl">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-bold text-slate-900 mb-2">About This Policy</h2>
                <p className="text-slate-600 leading-relaxed">
                  Straight Connect ("Straight Connect," "Company," "we," "our," or "us") provides a digital 
                  marketplace where drivers, dispatchers, logistics companies, and employers connect for 
                  transportation jobs, networking, and business opportunities. This Privacy Policy explains 
                  how we collect, use, verify, disclose, and safeguard personal information when you access 
                  or use our website, mobile applications, and services.
                </p>
                <p className="text-slate-600 mt-3 font-medium">
                  By accessing or using the Platform, you agree to the practices described in this Privacy Policy.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Policy Sections */}
          <div className="space-y-4">
            {/* Scope Section */}
            <SectionCard
              id="scope"
              title="Scope of Policy"
              icon={Globe}
              color="blue"
              isExpanded={expandedSections['scope']}
              onToggle={() => toggleSection('scope')}
            >
              <p className="text-slate-600 mb-4">This Privacy Policy applies to:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Users Covered</h4>
                  <BulletList items={[
                    'Website visitors',
                    'Registered users',
                    'CDL drivers',
                    'Owner operators',
                    'Dispatchers',
                    'Logistics companies',
                    'Employers and recruiters',
                    'Subscription members'
                  ]} />
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Data Sources</h4>
                  <BulletList items={[
                    'User accounts',
                    'Job applications',
                    'Driver verification',
                    'Ratings and reviews',
                    'Messaging and communications',
                    'Subscription services'
                  ]} />
                </div>
              </div>
            </SectionCard>

            {/* Information We Collect */}
            <SectionCard
              id="information-collected"
              title="Information We Collect"
              icon={Database}
              color="purple"
              isExpanded={expandedSections['information-collected']}
              onToggle={() => toggleSection('information-collected')}
            >
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                    <User className="h-4 w-4 text-purple-500" />
                    Personal Identification Information
                  </h4>
                  <BulletList items={[
                    'Full name',
                    'Email address',
                    'Phone number',
                    'Date of birth',
                    'Mailing address',
                    'Profile photo',
                    'Username or account ID'
                  ]} />
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-purple-500" />
                    Professional and Employment Information
                  </h4>
                  <p className="text-slate-600 mb-2">For logistics and driver matching services, we may collect:</p>
                  <BulletList items={[
                    'CDL license information and class',
                    'Driving history',
                    'TWIC card status',
                    'Endorsements',
                    'Truck type (box truck, tractor trailer, etc.)',
                    'Employer or dispatcher affiliation',
                    'Work history',
                    'Certifications and training'
                  ]} />
                </div>

                <div>
                  <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                    <Shield className="h-4 w-4 text-purple-500" />
                    Identity Verification Data
                  </h4>
                  <p className="text-slate-600 mb-2">To prevent fraud and ensure platform integrity, we may collect:</p>
                  <BulletList items={[
                    'Government-issued ID',
                    'Driver\'s license scans',
                    'Biometric verification',
                    'Facial recognition verification',
                    'Identity confirmation through Persona verification services'
                  ]} />
                  <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Identity verification is performed to ensure that users represent 
                      themselves truthfully and to protect the safety of other platform participants.
                    </p>
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* Persona Verification */}
            <SectionCard
              id="persona-verification"
              title="Third-Party Identity Verification (Persona)"
              icon={Shield}
              color="green"
              isExpanded={expandedSections['persona-verification']}
              onToggle={() => toggleSection('persona-verification')}
            >
              <p className="text-slate-600 mb-4">
                Straight Connect uses Persona identity verification technology to verify user identity.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-green-900 mb-2">When identity verification is required:</h4>
                <BulletList items={[
                  'Users will be redirected to Persona\'s verification system',
                  'Persona may collect biometric and identity data',
                  'Verification processes are governed by Persona\'s own privacy policy and data security standards'
                ]} />
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 text-sm">
                  <strong>Important:</strong> Straight Connect does not store biometric data generated during 
                  verification, unless required for fraud prevention or regulatory compliance. Users should 
                  review Persona's privacy policy before completing identity verification.
                </p>
              </div>
            </SectionCard>

            {/* Platform Profiles */}
            <SectionCard
              id="profiles"
              title="Platform Profiles"
              icon={User}
              color="cyan"
              isExpanded={expandedSections['profiles']}
              onToggle={() => toggleSection('profiles')}
            >
              <p className="text-slate-600 mb-4">Users may create professional profiles including:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <Truck className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <span className="font-medium text-blue-900">Driver Profiles</span>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                  <Phone className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                  <span className="font-medium text-purple-900">Dispatcher Profiles</span>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <Briefcase className="h-6 w-6 text-green-600 mx-auto mb-2" />
                  <span className="font-medium text-green-900">Carrier/Logistics Profiles</span>
                </div>
              </div>
              <p className="text-slate-600 mb-2">Profile information may include:</p>
              <BulletList items={[
                'Professional experience',
                'Driver credentials',
                'Endorsements',
                'Ratings',
                'Availability',
                'Preferred routes',
                'Vehicle information'
              ]} />
              <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  <AlertCircle className="h-4 w-4 inline mr-1 text-amber-500" />
                  Certain profile information may be visible to other users of the Platform.
                </p>
              </div>
            </SectionCard>

            {/* Ratings and Reviews */}
            <SectionCard
              id="ratings"
              title="Ratings and Reviews"
              icon={Eye}
              color="yellow"
              isExpanded={expandedSections['ratings']}
              onToggle={() => toggleSection('ratings')}
            >
              <p className="text-slate-600 mb-4">Straight Connect may allow:</p>
              <BulletList items={[
                'Driver ratings',
                'Dispatcher ratings',
                'Company ratings',
                'Service reviews'
              ]} />
              <p className="text-slate-600 my-4">
                These ratings help maintain accountability and transparency within the platform.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-900 mb-2">Reviews may be removed if they contain:</h4>
                <div className="grid grid-cols-2 gap-2">
                  {['Harassment', 'Discrimination', 'False information', 'Illegal content'].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-red-700 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </SectionCard>

            {/* Job Search */}
            <SectionCard
              id="jobs"
              title="Job Search and Recruitment"
              icon={Briefcase}
              color="indigo"
              isExpanded={expandedSections['jobs']}
              onToggle={() => toggleSection('jobs')}
            >
              <p className="text-slate-600 mb-4">
                The platform allows employers, carriers, and dispatchers to post:
              </p>
              <BulletList items={[
                'Job listings',
                'Contract opportunities',
                'Freight opportunities'
              ]} />
              <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <h4 className="font-semibold text-indigo-900 mb-2">When you apply for a job:</h4>
                <BulletList items={[
                  'Your profile information may be shared with the job poster',
                  'Your credentials may be reviewed by employers',
                  'Personal information is shared only for employment or contracting purposes authorized by you'
                ]} />
              </div>
            </SectionCard>

            {/* Credential Verification */}
            <SectionCard
              id="credentials"
              title="Credential Verification (CDL, TWIC, Truck Identification)"
              icon={FileText}
              color="teal"
              isExpanded={expandedSections['credentials']}
              onToggle={() => toggleSection('credentials')}
            >
              <p className="text-slate-600 mb-4">
                Straight Connect may verify certain professional credentials including:
              </p>
              <BulletList items={[
                'Commercial Driver\'s License (CDL)',
                'TWIC card eligibility',
                'Insurance information',
                'Truck registration',
                'Vehicle identification numbers',
                'Equipment certification'
              ]} />
              <p className="text-slate-600 my-4">Verification may occur through:</p>
              <BulletList items={[
                'User submitted documentation',
                'Manual review',
                'Third-party verification services'
              ]} />
              <div className="mt-3 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                <p className="text-sm text-teal-800">
                  Credential verification helps maintain safety and industry compliance.
                </p>
              </div>
            </SectionCard>

            {/* Subscriptions */}
            <SectionCard
              id="subscriptions"
              title="Subscription Services"
              icon={CreditCard}
              color="pink"
              isExpanded={expandedSections['subscriptions']}
              onToggle={() => toggleSection('subscriptions')}
            >
              <p className="text-slate-600 mb-4">
                Straight Connect may offer paid or subscription services including:
              </p>
              <BulletList items={[
                'Premium job access',
                'Featured driver profiles',
                'Business advertising',
                'Dispatch tools',
                'Lead generation'
              ]} />
              <div className="mt-4 p-4 bg-pink-50 border border-pink-200 rounded-lg">
                <p className="text-pink-800 text-sm">
                  <Lock className="h-4 w-4 inline mr-1" />
                  Subscription billing may involve third-party payment processors. Payment information is 
                  processed securely and Straight Connect does not store full credit card information.
                </p>
              </div>
            </SectionCard>

            {/* Auto-Collected Info */}
            <SectionCard
              id="auto-collected"
              title="Information Automatically Collected"
              icon={Database}
              color="orange"
              isExpanded={expandedSections['auto-collected']}
              onToggle={() => toggleSection('auto-collected')}
            >
              <p className="text-slate-600 mb-4">When using the platform we may collect:</p>
              <BulletList items={[
                'IP address',
                'Device information',
                'Browser type',
                'Location data',
                'Usage analytics',
                'Cookies and tracking technologies'
              ]} />
              <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h4 className="font-semibold text-orange-900 mb-2">Cookies help:</h4>
                <BulletList items={[
                  'Improve platform performance',
                  'Remember user preferences',
                  'Enhance security'
                ]} />
                <p className="text-orange-800 text-sm mt-2">
                  Users may disable cookies in their browser settings.
                </p>
              </div>
            </SectionCard>

            {/* How We Use Info */}
            <SectionCard
              id="how-we-use"
              title="How We Use Information"
              icon={CheckCircle}
              color="emerald"
              isExpanded={expandedSections['how-we-use']}
              onToggle={() => toggleSection('how-we-use')}
            >
              <p className="text-slate-600 mb-4">We use collected information to:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <BulletList items={[
                  'Operate and maintain the platform',
                  'Verify identity',
                  'Match drivers with job opportunities',
                  'Process subscriptions',
                  'Prevent fraud'
                ]} />
                <BulletList items={[
                  'Improve platform features',
                  'Communicate with users',
                  'Enforce platform rules',
                  'Comply with legal obligations'
                ]} />
              </div>
            </SectionCard>

            {/* Sharing */}
            <SectionCard
              id="sharing"
              title="Sharing of Information"
              icon={Users}
              color="violet"
              isExpanded={expandedSections['sharing']}
              onToggle={() => toggleSection('sharing')}
            >
              <p className="text-slate-600 mb-4">We may share information with:</p>
              <div className="space-y-4">
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                  <h4 className="font-semibold text-violet-900 mb-2">Employers or Job Posters</h4>
                  <p className="text-violet-800 text-sm">When users apply for jobs.</p>
                </div>
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                  <h4 className="font-semibold text-violet-900 mb-2">Service Providers</h4>
                  <p className="text-violet-800 text-sm mb-2">Companies that help operate our services including:</p>
                  <BulletList items={[
                    'Identity verification providers',
                    'Payment processors',
                    'Hosting providers',
                    'Analytics providers'
                  ]} />
                </div>
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                  <h4 className="font-semibold text-violet-900 mb-2">Legal Authorities</h4>
                  <p className="text-violet-800 text-sm">When required by law, subpoena, or court order.</p>
                </div>
              </div>
              <p className="text-slate-600 mt-4 text-sm">
                Personal information may also be shared during business transactions such as mergers or acquisitions.
              </p>
            </SectionCard>

            {/* Security */}
            <SectionCard
              id="security"
              title="Data Security"
              icon={Lock}
              color="red"
              isExpanded={expandedSections['security']}
              onToggle={() => toggleSection('security')}
            >
              <p className="text-slate-600 mb-4">
                We implement industry standard security safeguards including:
              </p>
              <BulletList items={[
                'Encrypted data transmission',
                'Secure servers',
                'Restricted employee access',
                'Identity verification controls'
              ]} />
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">
                  <AlertCircle className="h-4 w-4 inline mr-1" />
                  Despite our safeguards, no method of internet transmission is completely secure. 
                  Users are responsible for protecting their login credentials.
                </p>
              </div>
            </SectionCard>

            {/* User Rights */}
            <SectionCard
              id="user-rights"
              title="User Rights"
              icon={Scale}
              color="blue"
              isExpanded={expandedSections['user-rights']}
              onToggle={() => toggleSection('user-rights')}
            >
              <p className="text-slate-600 mb-4">Users may have rights including:</p>
              <BulletList items={[
                'Access to personal data',
                'Correction of inaccurate information',
                'Deletion of account data',
                'Data portability',
                'Opt-out of marketing communications'
              ]} />
              <p className="text-slate-600 mt-4">
                Requests can be submitted through our contact channels.
              </p>
            </SectionCard>

            {/* California Rights */}
            <SectionCard
              id="california"
              title="California Privacy Rights (CCPA/CPRA)"
              icon={Scale}
              color="amber"
              isExpanded={expandedSections['california']}
              onToggle={() => toggleSection('california')}
            >
              <p className="text-slate-600 mb-4">
                If you are a California resident, you may have rights including:
              </p>
              <BulletList items={[
                'The right to know what personal data is collected',
                'The right to request deletion',
                'The right to opt out of certain data sharing',
                'The right to non-discrimination for exercising privacy rights'
              ]} />
              <p className="text-slate-600 mt-4">
                Requests may be submitted through our privacy contact email.
              </p>
            </SectionCard>

            {/* Children's Privacy */}
            <SectionCard
              id="children"
              title="Children's Privacy"
              icon={Baby}
              color="rose"
              isExpanded={expandedSections['children']}
              onToggle={() => toggleSection('children')}
            >
              <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
                <p className="text-rose-800">
                  Straight Connect services are intended for individuals <strong>18 years of age or older</strong>.
                  We do not knowingly collect personal information from individuals under 18.
                </p>
              </div>
            </SectionCard>

            {/* Data Retention */}
            <SectionCard
              id="retention"
              title="Data Retention"
              icon={Clock}
              color="slate"
              isExpanded={expandedSections['retention']}
              onToggle={() => toggleSection('retention')}
            >
              <p className="text-slate-600 mb-4">
                We retain personal information only as long as necessary to:
              </p>
              <BulletList items={[
                'Provide platform services',
                'Comply with legal requirements',
                'Resolve disputes',
                'Enforce agreements'
              ]} />
              <p className="text-slate-600 mt-4">
                Account data may be deleted upon request subject to legal obligations.
              </p>
            </SectionCard>

            {/* Third Party */}
            <SectionCard
              id="third-party"
              title="Third-Party Websites"
              icon={ExternalLink}
              color="gray"
              isExpanded={expandedSections['third-party']}
              onToggle={() => toggleSection('third-party')}
            >
              <p className="text-slate-600">
                The Platform may contain links to third-party websites or services. Straight Connect is not 
                responsible for the privacy practices of third-party services, and users should review those 
                policies before providing information.
              </p>
            </SectionCard>

            {/* Changes */}
            <SectionCard
              id="changes"
              title="Changes to This Privacy Policy"
              icon={Bell}
              color="sky"
              isExpanded={expandedSections['changes']}
              onToggle={() => toggleSection('changes')}
            >
              <p className="text-slate-600 mb-4">
                We may update this Privacy Policy periodically. When changes occur we will:
              </p>
              <BulletList items={[
                'Update the "Last Updated" date',
                'Notify users when legally required'
              ]} />
              <p className="text-slate-600 mt-4">
                Continued use of the Platform constitutes acceptance of the updated policy.
              </p>
            </SectionCard>

            {/* Contact */}
            <SectionCard
              id="contact"
              title="Contact Information"
              icon={Mail}
              color="green"
              isExpanded={expandedSections['contact']}
              onToggle={() => toggleSection('contact')}
            >
              <p className="text-slate-600 mb-4">For privacy related inquiries contact:</p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <h4 className="font-bold text-green-900 mb-3">Straight Connect Privacy Department</h4>
                <div className="flex items-center gap-2 text-green-800">
                  <Mail className="h-5 w-5" />
                  <a href="mailto:telly.arnold@tlagroupllc.com" className="hover:underline">
                    telly.arnold@tlagroupllc.com
                  </a>
                </div>
              </div>
            </SectionCard>
          </div>

          {/* Legal Disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8 bg-slate-100 border border-slate-200 rounded-xl p-6"
          >
            <div className="flex items-start gap-4">
              <div className="bg-slate-200 p-3 rounded-xl">
                <Scale className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">Legal Disclaimer</h3>
                <p className="text-slate-600 text-sm">
                  This Privacy Policy is provided for informational purposes and does not constitute legal advice. 
                  Businesses should consult with qualified legal counsel to ensure full compliance with applicable 
                  federal, state, and international data protection laws.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Back to Top */}
          <div className="mt-8 text-center">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors"
            >
              Back to Top
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
